/**
 * Contracts for module "localization". Owner: ui-shell.
 * Per-user locale/timezone/currency preferences (QM-D08-001, QM-D08-003), tenant defaults, and
 * localized documents for reports and emails (QM-D08-002).
 */
import { z } from 'zod';

export const LocaleSchema = z.enum(['en', 'ar']);
export type LocaleCode = z.infer<typeof LocaleSchema>;
export const CurrencySchema = z.enum(['AED', 'SAR', 'USD', 'EUR', 'GBP', 'KWD', 'BHD', 'OMR', 'QAR']);

const isTimeZone = (tz: string) => {
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); return true; } catch { return false; }
};
export const TimeZoneSchema = z.string().min(1).max(64).refine(isTimeZone, 'invalid IANA timezone');

export const LocalePreferencesSchema = z.object({
  locale: LocaleSchema,
  timezone: TimeZoneSchema,
  currency: CurrencySchema,
  numberingSystem: z.enum(['latn', 'arab']).default('latn'),
});
export type LocalePreferences = z.infer<typeof LocalePreferencesSchema>;
export const UpdateLocalePreferencesSchema = LocalePreferencesSchema.partial().strict();

export const DEFAULT_LOCALE_PREFERENCES: LocalePreferences = { locale: 'en', timezone: 'Asia/Dubai', currency: 'AED', numberingSystem: 'latn' };

export const LocalePreferenceRecordSchema = LocalePreferencesSchema.extend({
  id: z.string(),
  userId: z.string(),
  source: z.enum(['user', 'tenant', 'default']),
  updatedAt: z.string().nullable(),
});
export type LocalePreferenceRecord = z.infer<typeof LocalePreferenceRecordSchema>;

// ---------- localized documents (reports / emails) ----------
export const DocumentBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('heading'), text: z.string().max(300) }),
  z.object({ type: z.literal('paragraph'), text: z.string().max(5000) }),
  z.object({ type: z.literal('metric'), label: z.string().max(200), value: z.number(), format: z.enum(['number', 'currency', 'percent']).default('number'), currency: CurrencySchema.optional() }),
  z.object({ type: z.literal('date'), label: z.string().max(200), at: z.string().datetime({ offset: true }) }),
]);
export const RenderDocumentSchema = z.object({
  kind: z.enum(['report', 'email']),
  locale: LocaleSchema.optional(),
  title: z.string().min(1).max(300),
  blocks: z.array(DocumentBlockSchema).max(500),
  /** reference to the source record in its own module (e.g. reports module id); opaque here */
  sourceRef: z.string().max(64).optional(),
});
export type RenderDocument = z.infer<typeof RenderDocumentSchema>;

/** Arabic-capable font stack. Inter Tight has no Arabic glyphs; IBM Plex Sans Arabic is embedded for ar. */
export const ARABIC_FONT_FAMILY = 'IBM Plex Sans Arabic';
